import { expandTitleLocales } from "@/services/partCategoryGroupsCatalog.js";

/** Canonical en/it/sq payload for cars (see docs on `partsByCategory`). */
export function pickLocaleTriplet(titleObj) {
  const t = titleObj || {};
  return {
    en: t.en != null ? String(t.en) : "",
    it: t.it != null ? String(t.it) : "",
    sq: t.sq != null ? String(t.sq) : "",
  };
}

export function normalizeTitleToken(s) {
  return typeof s === "string" ? s.trim().toLowerCase() : "";
}

export function categoryTitlesMatch(titleA, titleB) {
  const a = pickLocaleTriplet(titleA);
  const b = pickLocaleTriplet(titleB);
  return ["it", "en", "sq"].some(
    (L) =>
      normalizeTitleToken(a[L]) &&
      normalizeTitleToken(b[L]) &&
      normalizeTitleToken(a[L]) === normalizeTitleToken(b[L]),
  );
}

export function normalizeSnapshotCategoryId(v) {
  if (v == null || v === "") return "";
  return String(v).trim();
}

/**
 * Prefer stable DB id on each snapshot category block (matches Grafiche `categoryCode`).
 */
export function findCatalogCategoryForSnapshotBlock(block, catalogCategories) {
  if (!block || typeof block !== "object") return undefined;
  const rawId =
    block._id ??
    block.category_id ??
    block.categoryId ??
    block.categoryCode ??
    "";
  const id = normalizeSnapshotCategoryId(rawId);
  if (id && catalogCategories?.length) {
    const hit = catalogCategories.find(
      (c) => normalizeSnapshotCategoryId(c.categoryCode) === id,
    );
    if (hit) return hit;
  }
  if (block.category) {
    return findCatalogCategoryByTitlesDisambiguated(
      block.category,
      catalogCategories,
    );
  }
  return undefined;
}

/**
 * When `_id` is missing, match by it/en first (sq is often duplicated across categories).
 */
export function findCatalogCategoryByTitlesDisambiguated(
  savedCategoryTitles,
  catalogCategories,
) {
  const list = catalogCategories || [];
  const a = pickLocaleTriplet(savedCategoryTitles);
  const it = normalizeTitleToken(a.it);
  const en = normalizeTitleToken(a.en);
  if (it) {
    const m = list.filter(
      (c) => normalizeTitleToken(c.title?.it) === it && it.length > 0,
    );
    if (m.length === 1) return m[0];
  }
  if (en) {
    const m = list.filter(
      (c) => normalizeTitleToken(c.title?.en) === en && en.length > 0,
    );
    if (m.length === 1) return m[0];
  }
  return findCatalogCategoryForSnapshotCategory(savedCategoryTitles, list);
}

/**
 * Build `partsByCategory` car field from Grafiche selections in `vehicle.parts`.
 *
 * @param {Record<string, unknown>} vehicleParts – `vehicle.parts` (includes `comment` key + category keys).
 * @param {{ categoryCode: string, title?: object }[]} sortedCatalogCategories – Grafiche tabs order / codes.
 */
export function serializePartsByCategory(
  vehicleParts,
  sortedCatalogCategories,
) {
  if (!vehicleParts || typeof vehicleParts !== "object") {
    return { comment: "", categories: [] };
  }
  const comment =
    vehicleParts.comment != null ? String(vehicleParts.comment) : "";

  const categories = [];

  for (const cat of sortedCatalogCategories || []) {
    const key = cat.categoryCode;
    const lines = vehicleParts[key];
    if (!Array.isArray(lines) || !lines.length) continue;

    const partsRow = [];
    for (const line of lines) {
      if (!line || typeof line !== "object") continue;
      const code = Number(line.partCode);
      if (!Number.isFinite(code)) continue;
      const row = {
        partCode: code,
        title: pickLocaleTriplet(line.title),
        notes: line.notes != null ? String(line.notes) : "",
        selected: !!line.selected,
      };
      if (line._id != null) row._id = String(line._id);
      partsRow.push(row);
    }
    if (!partsRow.length) continue;

    categories.push({
      _id: String(key),
      category: pickLocaleTriplet(cat.title),
      parts: partsRow,
    });
  }

  return { comment, categories };
}

export function findCatalogCategoryForSnapshotCategory(
  savedCategoryTitles,
  catalogCategories,
) {
  return (catalogCategories || []).find((c) =>
    categoryTitlesMatch(c.title, savedCategoryTitles),
  );
}

/**
 * Applies saved `partsByCategory` onto `vehicle.parts` Grafiche keys using catalog templates when available.
 * @returns {boolean} Whether a snapshot was applied.
 */
export function mergeVehicleSlotsFromPartsByCategory({
  vehicle,
  snapshot,
  catalogCategories,
  lineTemplatesByCategory,
}) {
  if (
    !vehicle ||
    !snapshot ||
    !Array.isArray(snapshot.categories) ||
    !snapshot.categories.length
  ) {
    return false;
  }

  if (!vehicle.parts || typeof vehicle.parts !== "object") vehicle.parts = {};

  if (snapshot.comment != null && snapshot.comment !== undefined) {
    vehicle.parts.comment = String(snapshot.comment);
  }

  vehicle.partsByCategory = JSON.parse(JSON.stringify(snapshot));

  for (const block of snapshot.categories) {
    const matched = findCatalogCategoryForSnapshotBlock(
      block,
      catalogCategories || [],
    );
    const categoryId = matched?.categoryCode;
    const templates = categoryId
      ? lineTemplatesByCategory[categoryId]
      : undefined;
    const byCode = new Map(
      (block.parts || []).map((p) => [Number(p.partCode), p]),
    );

    let lines;
    if (Array.isArray(templates) && templates.length > 0) {
      lines = templates
        .filter((t) => t.enabled !== false)
        .map((t) => {
          const code = Number(t.partCode);
          const saved = byCode.get(code);
          return {
            _id: saved && saved._id != null ? String(saved._id) : String(t._id),
            partCode: code,
            title: saved?.title
              ? expandTitleLocales(saved.title)
              : expandTitleLocales(t.title),
            notes: saved && saved.notes != null ? String(saved.notes) : "",
            selected: !!(saved && saved.selected),
          };
        });
    } else {
      lines = (block.parts || []).map((p, idx) => ({
        _id:
          p._id != null
            ? String(p._id)
            : `pbc-${categoryId || "unmatched"}-${p.partCode}-${idx}`,
        partCode: Number(p.partCode),
        title: expandTitleLocales(p.title),
        notes: p.notes != null ? String(p.notes) : "",
        selected: !!p.selected,
      }));
    }

    if (categoryId) {
      vehicle.parts[categoryId] = lines;
    }
  }

  return true;
}
